import { PostgrestError } from "@supabase/supabase-js";
import React, { useEffect, useRef, useState } from "react";

import { DiscordMembers } from "../../../types/discord_members";
// eslint-disable-next-line import/extensions
import {
  getActiveCohortMembers,
  getCohortMembers,
  getDiscordRetentionData,
  getNewCohortMemberData,
} from "../../lib/discordQueries";
// eslint-disable-next-line import/extensions
import { RetentionData } from "../../lib/discordTypes";

/**
 * Formats the retention data from the RPC in in a more convenient way
 * to generate the retention chart
 * @param data the retention data returned from the RPC
 * @returns the formatted RPC data
 *
 * 0: ["Month",      "Members joined", "Month 0", "Month 1", "Month 2"]
 * 1: ["2021-12-01",  12,               50,        8.33,      8.33]
 * 2: ["2022-01-01", 35, 51.43, 2.86]
 * 3: ["2022-02-01", 29, 79.31]
 */
async function mapRetentionResults(data: RetentionData[]): Promise<any> {
  // group by time period
  const groupedObject = data.reduce((acc: any, value: RetentionData) => {
    if (!acc[value.cohort_month]) {
      acc[value.cohort_month] = [];
    }
    acc[value.cohort_month].push(value);
    return acc;
  }, {});

  // get first row with the months
  const firstRow = Object.keys(groupedObject).map((_item, index) => {
    return `Month ${index}`;
  });
  firstRow.unshift("Month", "Members joined");

  // format array to easily display the results
  const formattedArray = Object.keys(groupedObject).map((item) => {
    const rowData = groupedObject[item];

    rowData.unshift(item, groupedObject[item][0].members_joined);
    return rowData;
  });

  // add first row
  formattedArray.unshift(firstRow);
  return formattedArray;
}

/**
 * Get retention data from the RPC
 * @param guildId the guild id
 * @param interval the number of months to analyze
 * @param roleId role id to filter by
 * @returns
 * data: the retention data
 * isLoading: wether the hook is loading
 * filterMembers: callback to filter the current cohort's active and inactive members
 * activeCohortMembers: the current cohort's active members
 * inactiveCohortMembers: the current chort's inactive members
 * newChortMembers: the current cohort's new members
 * getNewCohortMembers: callback to load the current cohort's new members
 * getNewCohortMembersOnly: callback to load new cohort members from the parent only (not
 * including active and inactive members)
 */
function useRetentionRpc(
  guildId: string,
  interval: string,
  roleId?: string | null
) {
  const [loading, setLoading] = useState<boolean>(true);
  const [retentionData, setRetentionData] = useState<any>();
  const [activeCohortMembers, setActiveCohortMembers] = useState<
    DiscordMembers[]
  >([]);
  const [activeCohortMembersError, setActiveCohortMembersError] = useState<
    PostgrestError | { message: string } | null
  >();
  const [inactiveCohortMembers, setInactiveCohortMembers] = useState<
    DiscordMembers[]
  >([]);
  const [newCohortMembers, setNewCohortMembers] = useState<DiscordMembers[]>(
    []
  );
  const [newCohortMembersError, setNewCohortMembersError] =
    useState<PostgrestError | null>();
  const savedCohort = useRef("");

  const workspaceId: string =
    JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
      ?.currentSession.user?.app_metadata.workspace || "";

  // Load new cohort members data only
  const getNewCohortMembersOnly = async (
    cohort: string,
    roleId?: string | null
  ) => {
    setLoading(true);
    const newMembersData = await getNewCohortMemberData(
      cohort,
      guildId,
      roleId
    );
    if ("error" in newMembersData)
      setNewCohortMembersError(newMembersData.error);
    else setNewCohortMembers(newMembersData);
    setLoading(false);
  };

  const getAllCohortMembers = async (
    cohort: string,
    retentionMonth: number,
    roleId?: string | null
  ) => {
    setLoading(true);
    // First, get the cohort's new members.
    // Check if they are already available in state (check cohort value previously clicked)
    // If not, fetch them from the database
    let newCohortMembersDataFetcher;
    if (
      savedCohort.current === cohort &&
      savedCohort.current !== "" &&
      cohort !== ""
    ) {
      newCohortMembersDataFetcher = newCohortMembers;
    } else {
      newCohortMembersDataFetcher = getNewCohortMemberData(
        cohort,
        guildId,
        roleId
      );
      savedCohort.current = cohort;
    }
    // Next, get the cohort's active members
    const activeCohortMembersDataFetcher = getActiveCohortMembers(
      cohort,
      retentionMonth,
      guildId,
      roleId
    );
    // Actually perform the fetches (use Promise.all to do both at once)
    const [newCohortMembersData, activeCohortMembersData] = await Promise.all([
      newCohortMembersDataFetcher,
      activeCohortMembersDataFetcher,
    ]);
    // Filter inactive users
    const inactiveCohortMembersData: any[] = [];
    if (
      !("error" in newCohortMembersData) &&
      !("error" in activeCohortMembersData)
    ) {
      newCohortMembersData.forEach((item) => {
        let counter = 1;
        activeCohortMembersData.forEach((obj) => {
          if (obj.discord_id === item.discord_id) {
            counter += counter;
          }
        });
        if (counter === 1) inactiveCohortMembersData.push(item);
      });
    }

    if ("error" in activeCohortMembersData)
      setActiveCohortMembersError(activeCohortMembersData.error);
    else setActiveCohortMembers(activeCohortMembersData);
    setInactiveCohortMembers(inactiveCohortMembersData);
    if ("error" in newCohortMembersData)
      setNewCohortMembersError(newCohortMembersData.error);
    else setNewCohortMembers(newCohortMembersData);
    setLoading(false);
  };

  // update if start time or end time changes
  useEffect(() => {
    const getRetentionData = async (): Promise<void> => {
      setLoading(true);
      try {
        const data: RetentionData[] | null = await getDiscordRetentionData(
          guildId,
          workspaceId,
          interval,
          roleId
        );
        if (data && data.length) {
          // format the result in desired format for the chart
          const formattedResults = await mapRetentionResults(data);
          setRetentionData(formattedResults);
        } else {
          setRetentionData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    getRetentionData();
  }, [guildId, interval, roleId]);

  if (!guildId) {
    return {
      data: null,
      isLoading: false,
      getAllCohortMembers,
      activeCohortMembers: [],
      inactiveCohortMembers: [],
      newCohortMembers: [],
      getNewCohortMembersOnly,
    };
  }
  return {
    data: retentionData,
    isLoading: loading,
    getAllCohortMembers,
    activeCohortMembers,
    inactiveCohortMembers,
    newCohortMembers,
    getNewCohortMembersOnly,
    newCohortMembersError,
    activeCohortMembersError,
  };
}

export default useRetentionRpc;
